import type { DeliveryZone } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:types:graphql'

import React from 'react'
import { Link, generatePath, useParams } from 'react-router-dom'
import { Button, Table, Tag, Message } from 'rsuite'

import AppLoader from '@components/common/AppLoader'
import CommonStatusLabel from '@components/common/CommonStatusLabel'

import { useFetchAllDeliveryZones } from '@@partner:graphql/query/deliveryZone/allDeliveryZones.gql'
import { ROUTE_DELIVERY_ZONES_EDIT, ROUTE_DELIVERY_ZONES_CREATE } from '@@partner:config/routes'
import { DELIVERY_CLAIM_TYPE_LABELS } from '@@partner:helpers/deliveryClaim'
import { formatPrice } from '@@partner:helpers/util'

const Column = Table.Column
const HeaderCell = Table.HeaderCell
const Cell = Table.Cell

export interface ShopDeliveryZonesTableProps {
  shopId: GQL_ID
}

export default function ShopDeliveryZonesTable ({ shopId }: ShopDeliveryZonesTableProps) {
  const { brandSlug } = useParams<{ brandSlug: string }>()

  const { data, loading, error } = useFetchAllDeliveryZones({
    filter: {
      fields: [ 'shop_id' ],
      op: 'eq',
      value: shopId
    }
  })

  const deliveryZones = data?.allDeliveryZones?.edges ?? []

  if (loading) {
    return <AppLoader.Inner content="Загрузка зон доставки" />
  }

  if (error) {
    return <Message type="error">Не удалось загрузить зоны доставки</Message>
  }

  if (deliveryZones.length === 0) {
    return (
      <div>
        <Message type="info" className="mb-3">
          У этого магазина пока нет зон доставки
        </Message>
        <Button
          appearance="primary"
          as={Link}
          to={generatePath(ROUTE_DELIVERY_ZONES_CREATE, { brandSlug })}
        >
          Создать зону доставки
        </Button>
      </div>
    )
  }

  return (
    <div>
      <Table
        data={deliveryZones}
        autoHeight
        wordWrap="break-word"
      >
        <Column flexGrow={1} verticalAlign="middle">
          <HeaderCell>ID</HeaderCell>
          <Cell dataKey="id" />
        </Column>

        <Column flexGrow={2} verticalAlign="middle">
          <HeaderCell>Название</HeaderCell>
          <Cell dataKey="name" />
        </Column>

        <Column flexGrow={1} verticalAlign="middle">
          <HeaderCell>Статус</HeaderCell>
          <Cell>
            {(zone: DeliveryZone) => <CommonStatusLabel status={zone.status} />}
          </Cell>
        </Column>

        <Column flexGrow={1} verticalAlign="middle">
          <HeaderCell>Мин. заказ</HeaderCell>
          <Cell>
            {(zone: DeliveryZone) => formatPrice(zone.minOrderCost)}
          </Cell>
        </Column>

        <Column flexGrow={1} verticalAlign="middle">
          <HeaderCell>Стоимость</HeaderCell>
          <Cell>
            {(zone: DeliveryZone) => formatPrice(zone.delivery_cost ?? 0)}
          </Cell>
        </Column>

        <Column flexGrow={2} verticalAlign="middle" align="center">
          <HeaderCell>Провайдеры</HeaderCell>
          <Cell align="center">
            {(zone: DeliveryZone) => zone.deliveryProviders?.map(p => (
              <Tag key={p} size="sm" className="mr-1 mb-1">
                {DELIVERY_CLAIM_TYPE_LABELS.find(t => t.type === p)?.label ?? p}
              </Tag>
            ))}
          </Cell>
        </Column>

        <Column flexGrow={1} fixed="right" verticalAlign="middle">
          <HeaderCell>Действия</HeaderCell>
          <Cell>
            {(zone: DeliveryZone) => (
              <Button
                color="cyan"
                appearance="primary"
                as={Link}
                to={generatePath(ROUTE_DELIVERY_ZONES_EDIT, {
                  brandSlug,
                  zoneId: zone.id
                })}
                size="xs"
              >
                Редактировать
              </Button>
            )}
          </Cell>
        </Column>
      </Table>

      <div className="mt-3">
        <Button
          appearance="ghost"
          as={Link}
          to={generatePath(ROUTE_DELIVERY_ZONES_CREATE, { brandSlug })}
        >
          Добавить зону доставки
        </Button>
      </div>
    </div>
  )
}
